/**
 * Carbonize bundle resolver (2026-09-10)
 * ======================================
 * Answers one question and nothing else:
 *   "Given the products in a slot and what the customer picked so far, which choices are still possible,
 *    and which exact variant have they landed on?"
 * No HTML, no fetch() in this file - the bundle page and (later) the collection filter both use it.
 *
 * A slot's shape is derived from its products, never hardcoded by category:
 *   'single'         one product, one variant            -> no picker, auto-selected
 *   'product'        several products, one variant each  -> one picker (the material swatches)
 *   'model_material' products have real variants         -> two pickers (phone model + material)
 * A slot can be given `lockedOptionValue` (e.g. "Normal money clip"): every other variant is dropped first, so a
 * wallet slot genuinely becomes one-variant-per-product and derives 'product' on its own.
 */
(function (global) {
  'use strict';

  // Model names look like "17 PRO MAX", "S26 ULTRA", "A56", "15 Normal". Newest first, best tier first.
  var TIER_RANKS = [[/PRO\s*MAX/i, 0], [/PRO\s*XL/i, 0], [/ULTRA/i, 0], [/PRO/i, 1], [/MAX/i, 1], [/PLUS/i, 2], [/AIR/i, 2], [/MINI/i, 4]];

  function parseModelName(name) {
    var text = String(name || '').trim();
    var match = text.match(/^([A-Za-z]*)\s*(\d+)/);
    var tier = 3;
    for (var i = 0; i < TIER_RANKS.length; i++) {
      if (TIER_RANKS[i][0].test(text)) { tier = TIER_RANKS[i][1]; break; }
    }
    return { prefix: match ? match[1].toUpperCase() : '', number: match ? parseInt(match[2], 10) : -1, tier: tier, raw: text };
  }

  function compareModels(a, b) {
    if (a.brandOrder !== b.brandOrder) return a.brandOrder - b.brandOrder;
    var pa = parseModelName(a.value), pb = parseModelName(b.value);
    if (pa.prefix !== pb.prefix) return pa.prefix < pb.prefix ? 1 : -1; // "S" line before "A" line
    if (pa.number !== pb.number) return pb.number - pa.number;          // newest first
    if (pa.tier !== pb.tier) return pa.tier - pb.tier;
    return pa.raw.localeCompare(pb.raw);
  }

  function sameText(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  /* ------------------------------------------------------------------ slot */

  function BundleSlot(config) {
    this.index = config.index;
    this.label = config.label || '';
    this.collectionTitle = config.collectionTitle || '';
    this.prompt = config.prompt || '';
    this.lockedOptionValue = config.lockedOptionValue || null;
    this.hideUnavailableModels = !!config.hideUnavailableModels;

    this.products = (config.products || []).map(function (product) {
      var variants = product.variants || [];
      if (config.lockedOptionValue) {
        variants = variants.filter(function (v) { return sameText(v.optionValue, config.lockedOptionValue); });
      }
      return Object.assign({}, product, { variants: variants });
    }).filter(function (product) { return product.variants.length > 0; });

    this.shape = this._deriveShape();
    this.selection = { material: null, model: null };
  }

  BundleSlot.prototype._deriveShape = function () {
    if (this.products.length === 0) return 'empty';
    var everyProductHasOneVariant = this.products.every(function (p) { return p.variants.length === 1; });
    if (this.products.length === 1 && everyProductHasOneVariant) return 'single';
    if (everyProductHasOneVariant) return 'product';
    return 'model_material';
  };

  BundleSlot.prototype.isResolved = function () { return this.resolve() !== null; };

  /** The chosen variant, or null while the customer still has a decision left. */
  BundleSlot.prototype.resolve = function () {
    if (this.shape === 'empty') return null;
    if (this.shape === 'single') return this._describe(this.products[0], this.products[0].variants[0]);
    var product = this._selectedProduct();
    if (!product) return null;
    if (this.shape === 'product') return this._describe(product, product.variants[0]);
    if (!this.selection.model) return null;
    var variant = product.variants.filter(function (v) { return sameText(v.optionValue, this.selection.model); }, this)[0];
    return variant ? this._describe(product, variant) : null;
  };

  // "3K Twill" exists as an iPhone product AND a Samsung product sharing one swatch; the model tells them apart.
  BundleSlot.prototype._productsForMaterial = function () {
    if (!this.selection.material) return [];
    return this.products.filter(function (p) { return sameText(p.materialKey, this.selection.material); }, this);
  };

  BundleSlot.prototype._selectedProduct = function () {
    var candidates = this._productsForMaterial();
    if (candidates.length === 0) return null;
    if (!this.selection.model) return candidates[0];
    var self = this;
    return candidates.filter(function (p) {
      return p.variants.some(function (v) { return sameText(v.optionValue, self.selection.model); });
    })[0] || null;
  };

  BundleSlot.prototype._describe = function (product, variant) {
    if (!product || !variant) return null;
    return {
      slotIndex: this.index,
      variantId: variant.id,
      price: variant.price,
      available: variant.available,
      image: variant.image || product.featuredImage || null,
      productTitle: product.title,
      productHandle: product.handle,
      productUrl: product.url,
      brand: product.brand || '',
      materialName: product.materialName,
      modelName: this.shape === 'model_material' ? variant.optionValue : '',
      optionName: product.optionName
    };
  };

  /* -------------------------------------------------- choices to show / grey out
   * Both builders return: { value, label, image, brand, exists, available, disabled, selected }
   *   exists    - this combination is something we sell at all
   *   available - it exists but may be sold out
   *   disabled  - either of the above; greyed and struck through, never clickable
   */
  BundleSlot.prototype.getMaterialOptions = function () {
    var model = this.selection.model, self = this;
    var byMaterial = Object.create(null), order = [];
    this.products.forEach(function (product) {
      var key = String(product.materialKey).trim().toLowerCase();
      if (!byMaterial[key]) {
        byMaterial[key] = { value: product.materialKey, label: product.materialName || product.title, image: null, exists: false, available: false };
        order.push(byMaterial[key]);
      }
      var entry = byMaterial[key];
      if (!entry.image) entry.image = product.materialImage || product.featuredImage || null;
      product.variants.forEach(function (variant) {
        if (model && !sameText(variant.optionValue, model)) return;
        entry.exists = true;
        if (variant.available) entry.available = true;
      });
    });
    return order.map(function (entry) {
      return {
        value: entry.value, label: entry.label, image: entry.image, brand: '',
        exists: entry.exists, available: entry.available,
        disabled: !entry.exists || !entry.available,
        selected: sameText(self.selection.material, entry.value)
      };
    });
  };

  /** Every model across every product, flattened, deduped, grouped by brand, newest first. */
  BundleSlot.prototype.getModelOptions = function () {
    if (this.shape !== 'model_material') return [];
    var material = this.selection.material, self = this;
    var seen = Object.create(null), options = [];
    var brandOrder = Object.create(null), nextBrand = 0;

    this.products.forEach(function (product) {
      var brand = String(product.brand || product.optionName || '').trim().toLowerCase();
      if (!(brand in brandOrder)) brandOrder[brand] = nextBrand++;
    });

    this.products.forEach(function (product) {
      var brandLabel = product.brand || product.optionName || '';
      var brandKey = String(brandLabel).trim().toLowerCase();
      product.variants.forEach(function (variant) {
        var key = brandKey + '::' + String(variant.optionValue).trim().toLowerCase();
        if (!seen[key]) {
          seen[key] = { value: variant.optionValue, label: variant.optionValue, image: null, brand: brandLabel, brandOrder: brandOrder[brandKey], exists: false, available: false };
          options.push(seen[key]);
        }
        if (!material || sameText(product.materialKey, material)) {
          seen[key].exists = true;
          if (variant.available) seen[key].available = true;
        }
      });
    });

    options.sort(compareModels);
    return options.map(function (option) {
      return {
        value: option.value, label: option.label, image: null, brand: option.brand,
        exists: option.exists, available: option.available,
        disabled: !option.exists || !option.available,
        selected: sameText(self.selection.model, option.value)
      };
    });
  };

  /** Record a choice. A contradicting pick on one axis clears the other, so either order works. */
  BundleSlot.prototype.select = function (axis, value) {
    if (axis === 'material') {
      this.selection.material = value;
      if (this.selection.model) {
        var stillOffered = this.getModelOptions().some(function (o) { return sameText(o.value, this.selection.model) && o.exists; }, this);
        if (!stillOffered) this.selection.model = null;
      }
    } else if (axis === 'model') {
      this.selection.model = value;
      if (this.selection.material) {
        var stillValid = this.getMaterialOptions().some(function (o) { return sameText(o.value, this.selection.material) && o.exists; }, this);
        if (!stillValid) this.selection.material = null;
      }
    }
    return this.resolve();
  };

  BundleSlot.prototype.preselectFirstAvailable = function () {
    if (this.shape === 'single' || this.shape === 'empty') return this.resolve();
    var material = this.getMaterialOptions().filter(function (o) { return !o.disabled; })[0];
    if (material) this.select('material', material.value);
    if (this.shape === 'model_material') {
      var model = this.getModelOptions().filter(function (o) { return !o.disabled; })[0];
      if (model) this.select('model', model.value);
    }
    return this.resolve();
  };

  /* ---------------------------------------------------------------- bundle */

  function Bundle(config) {
    this.id = config.id;
    this.name = config.name;
    this.price = config.price;                    // cents (fixed bundle price)
    this.discountAmount = config.discountAmount || 0; // cents; when > 0 the price is fullTotal - discountAmount
    this.shellMedia = config.shellMedia || [];
    this.slots = (config.slots || []).map(function (slot) { return new BundleSlot(slot); });
  }

  Bundle.prototype.resolvedSlots = function () { return this.slots.map(function (s) { return s.resolve(); }); };
  Bundle.prototype.isComplete = function () { return this.slots.every(function (s) { return s.isResolved(); }); };
  Bundle.prototype.firstUnresolvedSlot = function () { return this.slots.filter(function (s) { return !s.isResolved(); })[0] || null; };

  Bundle.prototype.pricing = function () {
    var fullTotal = this.resolvedSlots().reduce(function (sum, r) { return sum + (r ? r.price : 0); }, 0);
    var bundlePrice = this.discountAmount > 0 ? Math.max(0, fullTotal - this.discountAmount) : this.price;
    var savings = fullTotal - bundlePrice;
    return { fullTotal: fullTotal, bundlePrice: bundlePrice, dynamic: this.discountAmount > 0, savings: savings, showSavings: this.isComplete() && savings > 0 };
  };

  /**
   * Cart payload for ONE POST to /cart/add.js. `bundleKey` is unique per add so two bundles in one cart can
   * always be told apart in the order (the discount itself is a native Shopify automatic discount).
   */
  Bundle.prototype.toCartItems = function (bundleKey) {
    if (!this.isComplete()) return null;
    var bundleId = this.id, bundleName = this.name;
    return this.resolvedSlots().map(function (r) {
      return {
        id: r.variantId,
        quantity: 1,
        properties: { _bundle_id: bundleId, _bundle_name: bundleName, _bundle_key: bundleKey }
      };
    });
  };

  global.BundleResolver = { Bundle: Bundle, BundleSlot: BundleSlot, compareModels: compareModels, parseModelName: parseModelName, sameText: sameText };
})(window);
